import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, Star, Trash2, X, ShieldAlert } from "lucide-react";
import { api } from "@/lib/api";
import { Badge, ETYPE_META, timeAgo } from "./engShared";
import { appConfirm } from "@/components/AppDialogHost";

export default function EngListeners() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const [detail, setDetail] = useState(null);
  const [notes, setNotes] = useState("");

  const load = () => api.get(`/engagement/listeners?q=${encodeURIComponent(q)}`)
    .then((r) => setItems(r.data.items || []));
  useEffect(() => { load(); }, []); // eslint-disable-line

  const open = async (l) => {
    setSel(l); setDetail(null);
    const r = await api.get(`/engagement/listeners/${l.listener_id}`);
    setDetail(r.data); setNotes(r.data.profile?.notes || "");
  };
  const toggleVip = async () => {
    const vip = !detail.profile.vip;
    await api.put(`/engagement/listeners/${sel.listener_id}`, { vip });
    setDetail((d) => ({ ...d, profile: { ...d.profile, vip } })); load();
  };
  const saveNotes = async () => {
    await api.put(`/engagement/listeners/${sel.listener_id}`, { notes });
    toast.success("Notes saved");
  };
  const purge = async () => {
    if (!await appConfirm("Permanently delete ALL data for this listener? Profile, activity and their name/email on past messages are removed. This can't be undone.")) return;
    await api.delete(`/engagement/listeners/${sel.listener_id}`);
    toast.success("Listener data deleted");
    setSel(null); load();
  };

  return (
    <div className="space-y-4" data-testid="eng-listeners-page">
      <div>
        <h1 className="font-display text-2xl font-bold">Listener Profiles</h1>
        <p className="text-sm text-ink-secondary">Built only from voluntary submissions — every profile can be fully deleted on request.</p>
      </div>
      <form onSubmit={(e) => { e.preventDefault(); load(); }}>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email…" data-testid="eng-listeners-search"
          className="rounded-md border border-line bg-bg-elevated px-2.5 py-1.5 text-xs w-64" />
      </form>

      <div className="rounded-xl border border-line bg-bg-surface divide-y divide-line/50">
        {!items.length && <div className="p-8 text-center text-sm text-ink-muted" data-testid="eng-listeners-empty">No listener profiles yet.</div>}
        {items.map((l) => (
          <button key={l.listener_id} onClick={() => open(l)} data-testid={`eng-listener-row-${l.listener_id}`}
            className="w-full text-left px-4 py-3 hover:bg-bg-elevated/40 flex items-center gap-3">
            <div className="h-8 w-8 rounded-full bg-bg-elevated border border-line flex items-center justify-center text-xs font-bold text-brand-cyan shrink-0">
              {(l.name || l.email || "?")[0]?.toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold flex items-center gap-1.5">
                {l.name || l.email}
                {l.vip && <Star className="h-3 w-3 text-brand-amber fill-brand-amber" />}
              </div>
              <div className="text-[11px] text-ink-muted">{l.email} · {l.counts?.total || 0} interactions · last seen {timeAgo(l.last_seen)}</div>
            </div>
          </button>
        ))}
      </div>

      {sel && (
        <div className="fixed inset-0 z-50 bg-black/60 flex justify-end" data-testid="eng-listener-detail">
          <div className="w-full max-w-md h-full bg-bg-surface border-l border-line overflow-y-auto p-5 space-y-4">
            <div className="flex items-start justify-between">
              <div>
                <div className="font-display font-bold flex items-center gap-2">{sel.name || sel.email}
                  {detail?.profile?.vip && <Star className="h-3.5 w-3.5 text-brand-amber fill-brand-amber" />}</div>
                <div className="text-[11px] text-ink-muted">{sel.email}{sel.phone ? ` · ${sel.phone}` : ""}</div>
              </div>
              <button onClick={() => setSel(null)} data-testid="eng-listener-close" className="text-ink-muted hover:text-ink-primary"><X className="h-5 w-5" /></button>
            </div>
            {!detail ? <div className="text-xs text-ink-muted">Loading…</div> : (<>
              <div className="grid grid-cols-3 gap-2 text-center">
                {Object.entries(detail.profile.counts || {}).filter(([k]) => k !== "total").map(([k, v]) => (
                  <div key={k} className="rounded-lg border border-line p-2">
                    <div className="text-sm font-bold">{v}</div>
                    <div className="text-[9px] font-mono uppercase text-ink-muted">{k.replace("_", " ")}</div>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <button onClick={toggleVip} data-testid="eng-listener-vip-btn"
                  className={`flex-1 inline-flex items-center justify-center gap-1.5 rounded-full px-3 py-2 text-xs font-bold border ${detail.profile.vip ? "bg-brand-amber text-black border-brand-amber" : "border-brand-amber/50 text-brand-amber"}`}>
                  <Star className="h-3.5 w-3.5" /> {detail.profile.vip ? "VIP listener" : "Mark VIP"}
                </button>
                <button onClick={purge} data-testid="eng-listener-purge-btn"
                  className="inline-flex items-center gap-1.5 rounded-full border border-rose-500/50 text-rose-300 px-3 py-2 text-xs font-bold">
                  <ShieldAlert className="h-3.5 w-3.5" /> Delete all data
                </button>
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted mb-1">Private notes</div>
                <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} data-testid="eng-listener-notes"
                  className="w-full rounded-md border border-line bg-bg-elevated px-2.5 py-2 text-xs" />
                <button onClick={saveNotes} data-testid="eng-listener-save-notes" className="mt-1 rounded-full border border-line px-3 py-1 text-[11px]">Save notes</button>
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted mb-1.5">Recent messages</div>
                {!detail.messages?.length && <div className="text-[11px] text-ink-muted">None.</div>}
                {(detail.messages || []).map((m) => (
                  <div key={m.message_id} className="border-b border-line/50 py-1.5">
                    <div className="flex items-center gap-1.5"><Badge meta={ETYPE_META[m.etype]} /><span className="text-[10px] text-ink-muted">{timeAgo(m.created_at)}</span></div>
                    <div className="text-[11px] text-ink-secondary mt-0.5 line-clamp-2">{m.message}</div>
                  </div>
                ))}
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted mb-1.5">Song requests</div>
                {!detail.requests?.length && <div className="text-[11px] text-ink-muted">None.</div>}
                {(detail.requests || []).map((r) => (
                  <div key={r.request_id} className="text-[11px] py-1 border-b border-line/50">{r.song_title} — {r.artist} <span className="text-ink-muted">({r.status})</span></div>
                ))}
              </div>
            </>)}
          </div>
        </div>
      )}
    </div>
  );
}
